import React, { useContext, useState } from 'react';
import ReactModal from 'react-modal';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { MdCheck, MdClose, MdOutlineVisibility, MdOutlineVisibilityOff } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';

import api from '../../services/api';
import Context, { IContext } from '../../context/Context';
import { cnpjMask, cpfMask, validaCpfCnpj } from '../../utils/validaCpfCnpj';
import { LINK_WHATSAPP } from './constants';

interface ICadastroModalProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ICampoProps extends React.InputHTMLAttributes<HTMLInputElement> {
  id: string;
  label: string;
  erro?: string;
  children?: React.ReactNode;
}

function Campo({ id, label, erro, children, className, ...props }: ICampoProps) {
  return (
    <div className={className}>
      <label htmlFor={id} className='mb-1.5 block text-sm font-medium text-zinc-700'>{label}</label>
      <div className='relative'>
        <input
          id={id}
          aria-invalid={!!erro}
          aria-describedby={erro ? `${id}-erro` : undefined}
          className={`h-12 w-full rounded-xl border bg-white px-4 text-zinc-900 placeholder:text-zinc-400 transition-colors focus:outline-none focus-visible:ring-2 ${erro ?
            'border-rose-300 focus-visible:ring-rose-500' :
            'border-zinc-200 hover:border-zinc-300 focus-visible:ring-zinc-900'
          }`}
          {...props}
        />
        {children}
      </div>
      {erro && <p id={`${id}-erro`} className='mt-1 text-xs text-rose-600'>{erro}</p>}
    </div>
  );
}

const formInicial = {
  raz: '',
  fan: '',
  cgc: '',
  caminho: '',
  usu: '',
  cgcUsu: '',
  sen: ''
};

export default function CadastroModal({ isOpen, onClose }: ICadastroModalProps) {
  const { setIsLoadingOverlay, setEmpresa }: IContext = useContext(Context);
  const navigate = useNavigate();
  const [formData, setFormData] = useState(formInicial);
  const [erros, setErros] = useState<{ cgc?: string, cgcUsu?: string }>({});
  const [mostrarSenha, setMostrarSenha] = useState(false);

  // Valida só quando o campo foi preenchido por completo, sem apagar o que o usuário digitou.
  function validarDocumento(campo: 'cgc' | 'cgcUsu', tamanho: number, nome: string) {
    const valor = formData[campo];
    const invalido = valor.length > 0 && (valor.length !== tamanho || !validaCpfCnpj(valor));
    setErros(atual => ({ ...atual, [campo]: invalido ? `${nome} inválido.` : undefined }));
    return !invalido;
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const cnpjValido = validarDocumento('cgc', 18, 'CNPJ');
    const cpfValido = validarDocumento('cgcUsu', 14, 'CPF');
    if (!cnpjValido) return toast.error('CNPJ inválido.');
    if (!cpfValido) return toast.error('CPF inválido.');

    try {
      setIsLoadingOverlay(true);

      const payloadEmpresa = {
        cgc: formData.cgc,
        raz: formData.raz,
        fan: formData.fan,
        caminho: formData.caminho
      };

      const responseEmpresa = await api.post('/empresas', payloadEmpresa);

      if (responseEmpresa.status === 201) {
        setEmpresa(responseEmpresa.data);
        const payloadUsuario = {
          usu: formData.usu,
          cgc: formData.cgcUsu,
          sen: formData.sen,
          tip: 'ADMINISTRADOR',
        };

        const responseUsuario = await api.post('/usuarios', payloadUsuario, {
          headers: {
            codemp: responseEmpresa.data.cod
          }
        });

        if (responseUsuario.status === 201) {
          api.interceptors.request.use(config => {
            const codemp = responseEmpresa.data.cod;
            if (config.headers && codemp) {
              config.headers['codemp'] = codemp;
            }
            return config;
          }, error => {
            return Promise.reject(error);
          });

          toast.success('Acesse utilizando CPF e Senha do responsável');
          setFormData(formInicial);
          onClose();
          navigate(`/${responseEmpresa.data.cod}/login`);
        }
      }

    } catch (error: any) {
      if (error.response?.data?.erro) return toast.error(error.response.data.erro);
      toast.error('Falha ao registrar conta.');
    } finally {
      setTimeout(() => {
        setIsLoadingOverlay(false);
      }, 500);
    }
  }

  return (
    <ReactModal
      isOpen={isOpen}
      appElement={document.getElementById('root') as HTMLElement}
      contentLabel='Cadastro do clube'
      shouldCloseOnOverlayClick={true}
      onRequestClose={onClose}
      overlayClassName='fixed inset-0 z-[99] flex items-end md:items-center justify-center bg-black/50 backdrop-blur-sm animate-fade-in motion-reduce:animate-none'
      className='relative flex w-full max-h-[94dvh] flex-col overflow-hidden rounded-t-2xl bg-white shadow-card outline-none animate-slide-up motion-reduce:animate-none md:max-w-xl md:rounded-2xl'
    >
      <div className='flex items-start justify-between gap-4 border-b border-zinc-100 px-5 py-4 md:px-6'>
        <div>
          <h2 className='text-lg font-semibold text-zinc-900'>Cadastre seu clube</h2>
          <p className='mt-0.5 text-sm text-zinc-500'>Leva menos de 2 minutos. Depois é só configurar seus espaços.</p>
        </div>
        <button
          type='button'
          onClick={onClose}
          aria-label='Fechar'
          className='-mr-2 flex h-10 w-10 flex-none items-center justify-center rounded-full text-zinc-500 hover:bg-zinc-100 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
        >
          <MdClose className='h-5 w-5' aria-hidden='true' />
        </button>
      </div>

      <form onSubmit={handleSubmit} className='flex min-h-0 flex-1 flex-col'>
        <div className='space-y-6 overflow-y-auto px-5 py-5 md:px-6'>
          <fieldset>
            <legend className='mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500'>
              <span className='flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-[11px] text-white'>1</span>
              Dados do clube
            </legend>
            <div className='grid gap-4 sm:grid-cols-2'>
              <Campo
                id='cad-cgc'
                label='CNPJ'
                placeholder='00.000.000/0000-00'
                inputMode='numeric'
                autoComplete='off'
                className='sm:col-span-2'
                value={formData.cgc}
                required
                maxLength={18}
                erro={erros.cgc}
                onChange={e => {
                  setFormData({ ...formData, cgc: cnpjMask(e.target.value) });
                  if (erros.cgc) setErros({ ...erros, cgc: undefined });
                }}
                onBlur={() => validarDocumento('cgc', 18, 'CNPJ')}
              />
              <Campo
                id='cad-raz'
                label='Razão social'
                placeholder='Associação Recreativa...'
                autoComplete='organization'
                value={formData.raz}
                required
                onChange={e => setFormData({ ...formData, raz: e.target.value })}
              />
              <Campo
                id='cad-fan'
                label='Nome fantasia'
                placeholder='Como os associados conhecem'
                value={formData.fan}
                required
                onChange={e => setFormData({ ...formData, fan: e.target.value })}
              />
              {/* <FormInput
              placeHolder='Caminho'
              value={formData.caminho}
              onChange={e => setFormData({ ...formData, caminho: e.target.value })}
            /> */}
            </div>
          </fieldset>

          <fieldset>
            <legend className='mb-3 flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500'>
              <span className='flex h-5 w-5 items-center justify-center rounded-full bg-zinc-900 text-[11px] text-white'>2</span>
              Responsável (administrador)
            </legend>
            <div className='grid gap-4 sm:grid-cols-2'>
              <Campo
                id='cad-usu'
                label='Nome completo'
                autoComplete='name'
                className='sm:col-span-2'
                value={formData.usu}
                required
                onChange={e => setFormData({ ...formData, usu: e.target.value })}
              />
              <Campo
                id='cad-cgcusu'
                label='CPF'
                placeholder='000.000.000-00'
                inputMode='numeric'
                autoComplete='off'
                value={formData.cgcUsu}
                required
                maxLength={14}
                erro={erros.cgcUsu}
                onChange={e => {
                  setFormData({ ...formData, cgcUsu: cpfMask(e.target.value) });
                  if (erros.cgcUsu) setErros({ ...erros, cgcUsu: undefined });
                }}
                onBlur={() => validarDocumento('cgcUsu', 14, 'CPF')}
              />
              <Campo
                id='cad-sen'
                label='Senha'
                type={mostrarSenha ? 'text' : 'password'}
                autoComplete='new-password'
                value={formData.sen}
                required
                onChange={e => setFormData({ ...formData, sen: e.target.value })}
              >
                <button
                  type='button'
                  onClick={() => setMostrarSenha(!mostrarSenha)}
                  aria-label={mostrarSenha ? 'Ocultar senha' : 'Mostrar senha'}
                  className='absolute inset-y-0 right-1 my-auto flex h-10 w-10 items-center justify-center rounded-lg text-zinc-500 hover:text-zinc-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900'
                >
                  {mostrarSenha ?
                    <MdOutlineVisibilityOff className='h-5 w-5' aria-hidden='true' /> :
                    <MdOutlineVisibility className='h-5 w-5' aria-hidden='true' />
                  }
                </button>
              </Campo>
            </div>
            <p className='mt-3 flex items-start gap-2 text-xs text-zinc-500'>
              <MdCheck className='mt-0.5 h-4 w-4 flex-none text-emerald-600' aria-hidden='true' />
              Você vai entrar no sistema com este CPF e senha para cadastrar espaços, preços e usuários.
            </p>
          </fieldset>
        </div>

        <div className='border-t border-zinc-100 bg-zinc-50/60 px-5 py-4 pb-[calc(1rem+env(safe-area-inset-bottom))] md:px-6 md:pb-4'>
          <button
            type='submit'
            className='h-12 w-full rounded-xl bg-zinc-900 font-semibold text-white transition-colors hover:bg-zinc-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 focus-visible:ring-offset-2'
          >
            Criar conta do clube
          </button>
          <p className='mt-3 text-center text-sm text-zinc-500'>
            Prefere tirar dúvidas antes?{' '}
            <a
              href={LINK_WHATSAPP}
              target='_blank'
              rel='noreferrer'
              className='inline-flex items-center gap-1 font-medium text-emerald-700 underline-offset-2 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-zinc-900 rounded'
            >
              <FaWhatsapp className='h-4 w-4' aria-hidden='true' />
              Fale no WhatsApp
            </a>
          </p>
        </div>
      </form>
    </ReactModal>
  );
}
